import { Mail, Phone, MapPin, Linkedin, Globe, Github, Calendar, Award } from 'lucide-react'
import { getColorClasses } from '@/lib/templateColors'

interface ResumeTemplateProps {
  data: any
  language?: string
  labels: any
  color?: string
}


export default function CreativeTemplate({ data, labels, color = 'blue' }: ResumeTemplateProps) {
  const colors = getColorClasses(color)
  const { personalInfo, summary, experience, education, skills, projects, certifications } = data

  return (

      <div className="bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
        <div id="resume-preview" className="bg-white" style={{ width: '8.5in', minHeight: '11in' }}>
          <div className="flex">
            {/* Dark Sidebar - Left */}
            <div className={`w-1/3 bg-gradient-to-b ${colors.headerBg} ${colors.headerText} p-6`}>
              {/* Photo Placeholder */}
              {personalInfo.photo && (
                <div className="mb-6">
                  <img src={personalInfo.photo} alt="Profile" className="w-32 h-32 rounded-full mx-auto border-4 border-white shadow-lg" />
                </div>
              )}

              {/* Contact Info */}
              <div className="mb-6">
                <h3 className={`text-lg font-bold mb-3 pb-2 border-b-2 ${colors.sectionBorder}`}>{labels.contact}</h3>
                <div className="space-y-2 text-sm">
                  {personalInfo.email && (
                    <div className="flex items-center space-x-2">
                      <Mail className="w-4 h-4 flex-shrink-0" />
                      <span className="break-all">{personalInfo.email}</span>
                    </div>
                  )}
                  {personalInfo.phone && (
                    <div className="flex items-center space-x-2">
                      <Phone className="w-4 h-4 flex-shrink-0" />
                      <span>{personalInfo.phone}</span>
                    </div>
                  )}
                  {personalInfo.address && (
                    <div className="flex items-center space-x-2">
                      <MapPin className="w-4 h-4 flex-shrink-0" />
                      <span>{personalInfo.address}</span>
                    </div>
                  )}
                  {personalInfo.linkedin && (
                    <div className="flex items-center space-x-2">
                      <Linkedin className="w-4 h-4 flex-shrink-0" />
                      <span className="break-all">{personalInfo.linkedin}</span>
                    </div>
                  )}
                  {personalInfo.website && (
                    <div className="flex items-center space-x-2">
                      <Globe className="w-4 h-4 flex-shrink-0" />
                      <span className="break-all">{personalInfo.website}</span>
                    </div>
                  )}
                  {personalInfo.github && (
                    <div className="flex items-center space-x-2">
                      <Github className="w-4 h-4 flex-shrink-0" />
                      <span className="break-all">{personalInfo.github}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Skills in Sidebar */}
              {skills && skills.length > 0 && (
                <div className="mb-6">
                  <h3 className={`text-lg font-bold mb-3 pb-2 border-b-2 ${colors.sectionBorder}`}>{labels.skills}</h3>
                  <div className="space-y-4">
                    {skills.map((skillGroup: any, idx: number) => (
                      <div key={idx}>
                        <div className={`text-sm font-bold mb-2 ${colors.accentText}`}>{skillGroup.category}</div>
                        <div className="space-y-1">
                          {skillGroup.skills?.map((skill: string, skillIdx: number) => (
                            <div key={skillIdx} className={`text-sm ${colors.badgeBg} bg-opacity-50 px-2 py-1 rounded`}>
                              {skill}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Education in Sidebar */}
              {education && education.length > 0 && (
                <div>
                  <h3 className={`text-lg font-bold mb-3 pb-2 border-b-2 ${colors.sectionBorder}`}>{labels.education}</h3>
                  <div className="space-y-3">
                    {education.map((edu: any) => (
                      <div key={edu.id} className="text-sm">
                        <div className="font-bold">{edu.degree}</div>
                        <div className={colors.accentText}>{edu.institution}</div>
                        <div className="text-xs opacity-80">{edu.graduationDate}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Main Content - Right */}
            <div className="w-2/3 p-8">
              {/* Header */}
              <div className={`mb-6 pb-4 border-b-4 ${colors.accentBorder}`}>
                <h1 className="text-4xl font-bold text-gray-900 mb-2">{personalInfo.fullName || 'YOUR NAME'}</h1>
                <div className={`text-xl ${colors.sectionTitle} font-semibold`}>Professional Profile</div>
              </div>

              {/* Summary */}
              {summary && (
                <div className="mb-6">
                  <h2 className={`text-2xl font-bold ${colors.sectionTitle} mb-3`}>{labels.professionalSummary}</h2>
                  <p className="text-gray-700 leading-relaxed text-justify">{summary}</p>
                </div>
              )}

              {/* Experience */}
              {experience && experience.length > 0 && (
                <div className="mb-6">
                  <h2 className={`text-2xl font-bold ${colors.sectionTitle} mb-4`}>{labels.workExperience}</h2>
                  <div className="space-y-5">
                    {experience.map((exp: any) => (
                      <div key={exp.id} className={`border-l-4 ${colors.accentBorder} pl-4`}>
                        <div className="flex justify-between items-start flex-wrap gap-2 mb-2">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">{exp.jobTitle}</h3>
                            <div className={`${colors.accentText} font-semibold`}>{exp.company}</div>
                          </div>
                          <div className="text-sm text-gray-600 flex items-center space-x-1">
                            <Calendar className="w-4 h-4" />
                            <span>{exp.startDate} - {exp.endDate || labels.present}</span>
                          </div>
                        </div>
                        {exp.location && (
                          <div className="text-sm text-gray-600 flex items-center space-x-1 mb-2">
                            <MapPin className="w-3 h-3" />
                            <span>{exp.location}</span>
                          </div>
                        )}
                        <div className="text-gray-700 text-sm whitespace-pre-line leading-relaxed">
                          {exp.description}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Projects */}
              {projects && projects.length > 0 && (
                <div className="mb-6">
                  <h2 className={`text-2xl font-bold ${colors.sectionTitle} mb-4`}>{labels.projects}</h2>
                  <div className="space-y-4">
                    {projects.map((project: any) => (
                      <div key={project.id}>
                        <h3 className="text-lg font-bold text-gray-900 mb-1">{project.name}</h3>
                        <p className="text-gray-700 text-sm mb-2">{project.description}</p>
                        {project.technologies && (
                          <div className="flex flex-wrap gap-2">
                            {project.technologies.map((tech: string, idx: number) => (
                              <span key={idx} className={`text-xs ${colors.badgeBg} ${colors.badgeText} px-2 py-1 rounded-full border ${colors.sectionBorder}`}>
                                {tech}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Certifications */}
              {certifications && certifications.length > 0 && (
                <div>
                  <h2 className={`text-2xl font-bold ${colors.sectionTitle} mb-4`}>{labels.certifications}</h2>
                  <div className="space-y-3">
                    {certifications.map((cert: any) => (
                      <div key={cert.id} className="flex items-start space-x-3">
                        <Award className={`w-5 h-5 ${colors.accentText} mt-0.5 flex-shrink-0`} />
                        <div>
                          <div className="font-bold text-gray-900">{cert.name}</div>
                          <div className="text-sm text-gray-600">{cert.issuer} • {cert.date}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
  )
}
